import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, Phone, MessageCircle, Check, X, Ban, CalendarClock,
  Clock, Calendar, Zap, Loader2, RefreshCw, AlertTriangle,
  Stethoscope, CheckCircle2, SlidersHorizontal, Edit3, ShieldAlert,
  Wallet
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inr, telLink, waLink, todayIST, fmtDate, fmtDateTime, initials } from "@/lib/format";
import { PageHeader, Loading, StatusBadge, EmptyState, AccountBalanceBadge } from "@/components/common";
import TreatmentRecordDialog from "@/components/TreatmentRecordDialog";
import PaymentDialog from "@/components/PaymentDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

// Summary cards configuration with merged Absent & Cancelled/Rescheduled plus Collect X card

const ABSENT_REASONS = [
  "Patient did not come",
  "Patient informed in advance",
  "Medical reason / unwell",
  "Traveling / Out of station",
  "Other",
];

const CANCEL_REASONS = [
  "Clinic unavailable / Closed",
  "Therapist unavailable",
  "Patient cancelled in advance",
  "Emergency",
  "Other",
];

export default function TodaysClinic() {
  const { user, settings } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  // Modal States
  const [treatmentOpen, setTreatmentOpen] = useState(false);
  const [selectedForTreatment, setSelectedForTreatment] = useState(null);

  const [absentOpen, setAbsentOpen] = useState(false);
  const [selectedForAbsent, setSelectedForAbsent] = useState(null);
  const [absentReason, setAbsentReason] = useState(ABSENT_REASONS[0]);
  const [absentNote, setAbsentNote] = useState("");
  const [markingAbsent, setMarkingAbsent] = useState(false);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [selectedForCancel, setSelectedForCancel] = useState(null);
  const [cancelReason, setCancelReason] = useState(CANCEL_REASONS[0]);
  const [cancelNote, setCancelNote] = useState("");
  const [markingCancel, setMarkingCancel] = useState(false);

  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [selectedAtt, setSelectedAtt] = useState(null);
  const [newVisitDate, setNewVisitDate] = useState("");
  const [newVisitTime, setNewVisitTime] = useState("");
  const [rescheduleReason, setRescheduleReason] = useState("");
  const [excludeUntilDate, setExcludeUntilDate] = useState(true);
  const [rescheduling, setRescheduling] = useState(false);

  const [comboOpen, setComboOpen] = useState(false);
  const [selectedForCombo, setSelectedForCombo] = useState(null);
  const [comboSummary, setComboSummary] = useState("");
  const [comboPainScore, setComboPainScore] = useState("");
  const [comboAdvice, setComboAdvice] = useState("");
  const [comboSendWa, setComboSendWa] = useState(false);
  const [savingCombo, setSavingCombo] = useState(false);

  const [correctOpen, setCorrectOpen] = useState(false);
  const [selectedForCorrect, setSelectedForCorrect] = useState(null);
  const [correctStatus, setCorrectStatus] = useState("present");
  const [correctReason, setCorrectReason] = useState("");
  const [correctNote, setCorrectNote] = useState("");
  const [savingCorrect, setSavingCorrect] = useState(false);

  const [cutoffOpen, setCutoffOpen] = useState(false);
  const [cutoffPreview, setCutoffPreview] = useState(null);
  const [loadingCutoffPreview, setLoadingCutoffPreview] = useState(false);
  const [runningCutoff, setRunningCutoff] = useState(false);

  const [paymentOpen, setPaymentOpen] = useState(false);
  const [selectedForPayment, setSelectedForPayment] = useState(null);
  const [defaultPaymentAmount, setDefaultPaymentAmount] = useState(null);

  const today = todayIST();

  const load = useCallback(async () => {
    setError(null);
    try {
      const [a, s] = await Promise.all([
        api.get("/attendance/today", { params: { date: today } }),
        api.get("/attendance/summary", { params: { date: today } }),
      ]);
      setItems(a.data.items);
      setSummary(s.data);
    } catch (err) {
      setError(apiError(err, "Failed to load today's clinic attendance"));
    } finally {
      setLoading(false);
    }
  }, [today]);

  useEffect(() => { load(); }, [load]);

  // Fast attendance marks
  const markPresent = async (att) => {
    setBusy(att.id + "present");
    try {
      const { data } = await api.post(`/attendance/${att.id}/mark`, { status: "present" });
      if (data.charge) {
        const bal = data.charge.account_balance;
        const statusText = bal > 0 ? `Credit ${inr(bal)}` : (bal < 0 ? `Due ${inr(Math.abs(bal))}` : "Settled");
        toast.success(`${att.patient_name}: Present · Fee ${inr(data.charge.charged)} (${statusText})`);
      } else {
        toast.success(`${att.patient_name}: Marked Present`);
      }
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  const openAbsentModal = (att) => {
    setSelectedForAbsent(att);
    setAbsentReason(ABSENT_REASONS[0]);
    setAbsentNote("");
    setAbsentOpen(true);
  };

  const handleConfirmAbsent = async () => {
    if (!selectedForAbsent) return;
    setMarkingAbsent(true);
    try {
      const reasonText = absentReason === "Other" ? (absentNote || "Other reason") : absentReason;
      await api.post(`/attendance/${selectedForAbsent.id}/mark`, {
        status: "absent",
        reason: reasonText,
        notes: absentNote,
      });
      toast.success(`${selectedForAbsent.patient_name}: Marked Absent (${reasonText})`);
      setAbsentOpen(false);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setMarkingAbsent(false);
    }
  };

  const openCancelModal = (att) => {
    setSelectedForCancel(att);
    setCancelReason(CANCEL_REASONS[0]);
    setCancelNote("");
    setCancelOpen(true);
  };

  const handleConfirmCancel = async () => {
    if (!selectedForCancel) return;
    setMarkingCancel(true);
    try {
      const reasonText = cancelReason === "Other" ? (cancelNote || "Other reason") : cancelReason;
      await api.post(`/attendance/${selectedForCancel.id}/mark`, {
        status: "cancelled",
        reason: reasonText,
        notes: cancelNote,
      });
      toast.success(`${selectedForCancel.patient_name}: Session Cancelled (${reasonText})`);
      setCancelOpen(false);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setMarkingCancel(false);
    }
  };

  const openReschedule = (att) => {
    setSelectedAtt(att);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setNewVisitDate(tomorrow.toISOString().slice(0, 10));
    setNewVisitTime("");
    setRescheduleReason("");
    setExcludeUntilDate(true);
    setRescheduleOpen(true);
  };

  const handleReschedule = async () => {
    if (!newVisitDate) {
      toast.error("Please select a new visit date");
      return;
    }
    setRescheduling(true);
    try {
      await api.post(`/attendance/${selectedAtt.id}/reschedule`, {
        new_date: newVisitDate,
        new_time: newVisitTime,
        reason: rescheduleReason,
        exclude_until_new_date: excludeUntilDate,
      });
      toast.success(`Session for ${selectedAtt.patient_name} rescheduled to ${fmtDate(newVisitDate)}`);
      setRescheduleOpen(false);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRescheduling(false);
    }
  };

  const openComboModal = (att) => {
    setSelectedForCombo(att);
    setComboSummary("Treatment successfully completed. Full functional recovery.");
    setComboPainScore("0");
    setComboAdvice("Continue daily home core strengthening and posture maintenance.");
    setComboSendWa(false);
    setComboOpen(true);
  };

  const handleConfirmCombo = async () => {
    if (!selectedForCombo) return;
    setSavingCombo(true);
    try {
      await api.post(`/attendance/${selectedForCombo.id}/present-and-complete`, {
        completion_summary: comboSummary,
        final_pain_score: comboPainScore !== "" ? parseInt(comboPainScore) : null,
        home_exercise_advice: comboAdvice,
        send_whatsapp: comboSendWa,
      });
      toast.success(`${selectedForCombo.patient_name}: Present + Treatment Completed!`);
      setComboOpen(false);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSavingCombo(false);
    }
  };

  const openCorrectModal = (att) => {
    setSelectedForCorrect(att);
    setCorrectStatus(att.status === "present" ? "absent" : "present");
    setCorrectReason("");
    setCorrectNote("");
    setCorrectOpen(true);
  };

  const handleConfirmCorrect = async () => {
    if (!correctReason || correctReason.trim().length < 3) {
      toast.error("Please provide a valid reason (minimum 3 characters)");
      return;
    }
    setSavingCorrect(true);
    try {
      await api.post(`/attendance/${selectedForCorrect.id}/correct`, {
        status: correctStatus,
        reason: correctReason.trim(),
        notes: correctNote,
      });
      toast.success(`Attendance corrected to ${correctStatus.toUpperCase()}`);
      setCorrectOpen(false);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSavingCorrect(false);
    }
  };

  const openCutoffModal = async () => {
    setLoadingCutoffPreview(true);
    setCutoffOpen(true);
    try {
      const { data } = await api.get("/attendance/cutoff-preview", { params: { date: today } });
      setCutoffPreview(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoadingCutoffPreview(false);
    }
  };

  const handleExecuteCutoff = async () => {
    setRunningCutoff(true);
    try {
      const { data } = await api.post("/attendance/run-cutoff", null, { params: { date: today } });
      toast.success(`${data.marked_absent} pending patients marked Automatic Absent`);
      setCutoffOpen(false);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRunningCutoff(false);
    }
  };

  const pendingCollectPatients = useMemo(() => {
    return items.filter((i) => {
      if (i.status !== "present" && i.status !== "completed") return false;
      const bal = Number(i.account_balance) || 0;
      return bal < 0;
    });
  }, [items]);

  const collectX = useMemo(() => {
    return pendingCollectPatients.reduce((sum, i) => {
      const fee = Number(i.session_fee) || 500;
      const bal = Number(i.account_balance) || 0;
      return sum + (bal < 0 ? Math.min(fee, Math.abs(bal)) : 0);
    }, 0);
  }, [pendingCollectPatients]);

  const filtered = useMemo(() => {
    let list = items;
    if (statusFilter !== "all") {
      if (statusFilter === "absent") {
        list = list.filter((i) => i.status === "absent");
      } else if (statusFilter === "cancelled_rescheduled") {
        list = list.filter((i) => i.status === "cancelled" || i.status === "rescheduled");
      } else if (statusFilter === "pending_payment") {
        list = list.filter((i) => (i.status === "present" || i.status === "completed") && Number(i.account_balance) < 0);
      } else {
        list = list.filter((i) => i.status === statusFilter);
      }
    }
    if (serviceFilter !== "all") {
      list = list.filter((i) => (i.service || "clinic") === serviceFilter);
    }
    const q = search.toLowerCase().trim();
    if (q) {
      list = list.filter(
        (i) =>
          (i.patient_name || "").toLowerCase().includes(q) ||
          (i.phone || "").includes(q) ||
          (i.patient_code || "").toLowerCase().includes(q) ||
          (i.problem || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [items, statusFilter, serviceFilter, search]);

  if (loading) return <Loading label="Preparing today's clinic..." />;

  if (error) {
    return (
      <div className="p-8 text-center bg-red-50 rounded-xl border border-red-200 my-6">
        <AlertTriangle className="h-10 w-10 text-red-500 mx-auto mb-2" />
        <h3 className="font-display text-lg font-bold text-red-800">Unable to load Today's Clinic</h3>
        <p className="text-sm text-red-600 mb-4">{error}</p>
        <Button onClick={load} variant="outline" className="gap-2">
          <RefreshCw className="h-4 w-4" /> Retry
        </Button>
      </div>
    );
  }

  const isAdmin = user?.role === "admin" || user?.role === "owner";

  return (
    <div>
      <PageHeader
        title="Today's Clinic"
        subtitle={`${fmtDate(today)} · Auto-absent cutoff at ${settings?.attendance_cutoff || "22:00"} IST`}
        testid="clinic-page"
      >
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} title="Refresh attendance list">
            <RefreshCw className="h-4 w-4" />
          </Button>
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={openCutoffModal}
              data-testid="run-cutoff-btn"
              className="text-bk-danger border-red-200 hover:bg-red-50"
            >
              <Zap className="h-4 w-4 mr-1" /> Run Cutoff Now
            </Button>
          )}
        </div>
      </PageHeader>

      {/* Summary Cards: Merged Absent, Merged Cancel/Rescheduled, and Collect X */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 mb-4">
        {/* 1. Expected */}
        <Card
          onClick={() => setStatusFilter("all")}
          className={`p-3 text-center cursor-pointer transition-all hover:shadow-sm ${
            statusFilter === "all" ? "ring-2 ring-bk-blue bg-blue-50/40 border-bk-blue" : "bg-white"
          }`}
          data-testid="summary-expected"
        >
          <p className="font-display font-extrabold text-2xl tabular-nums text-bk-blue">
            {summary.expected ?? items.length}
          </p>
          <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mt-0.5">Expected</p>
          <p className="text-[10px] text-slate-400 mt-0.5">Today's Clinic</p>
        </Card>

        {/* 2. Present */}
        <Card
          onClick={() => setStatusFilter("present")}
          className={`p-3 text-center cursor-pointer transition-all hover:shadow-sm ${
            statusFilter === "present" ? "ring-2 ring-emerald-500 bg-emerald-50/40 border-emerald-500" : "bg-white"
          }`}
          data-testid="summary-present"
        >
          <p className="font-display font-extrabold text-2xl tabular-nums text-bk-green">
            {summary.present ?? 0}
          </p>
          <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mt-0.5">Present</p>
          <p className="text-[10px] text-emerald-600 mt-0.5 font-medium">Attending today</p>
        </Card>

        {/* 3. Merged Absent (Manual + Auto) */}
        <Card
          onClick={() => setStatusFilter("absent")}
          className={`p-3 text-center cursor-pointer transition-all hover:shadow-sm ${
            statusFilter === "absent" ? "ring-2 ring-red-500 bg-red-50/40 border-red-500" : "bg-white"
          }`}
          data-testid="summary-absent"
        >
          <p className="font-display font-extrabold text-2xl tabular-nums text-bk-danger">
            {(summary.manual_absent || 0) + (summary.auto_absent || 0)}
          </p>
          <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mt-0.5">Absent</p>
          <p className="text-[10px] text-slate-400 mt-0.5">
            Manual: {summary.manual_absent || 0} · Auto: {summary.auto_absent || 0}
          </p>
        </Card>

        {/* 4. Merged Cancelled / Rescheduled */}
        <Card
          onClick={() => setStatusFilter("cancelled_rescheduled")}
          className={`p-3 text-center cursor-pointer transition-all hover:shadow-sm ${
            statusFilter === "cancelled_rescheduled" ? "ring-2 ring-indigo-500 bg-indigo-50/40 border-indigo-500" : "bg-white"
          }`}
          data-testid="summary-cancelled-rescheduled"
        >
          <p className="font-display font-extrabold text-2xl tabular-nums text-indigo-700">
            {(summary.cancelled || 0) + (summary.rescheduled || 0)}
          </p>
          <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mt-0.5">Cancelled / Rescheduled</p>
          <p className="text-[10px] text-slate-400 mt-0.5">
            Resched: {summary.rescheduled || 0} · Cancel: {summary.cancelled || 0}
          </p>
        </Card>

        {/* 5. Pending */}
        <Card
          onClick={() => setStatusFilter("pending")}
          className={`p-3 text-center cursor-pointer transition-all hover:shadow-sm ${
            statusFilter === "pending" ? "ring-2 ring-slate-600 bg-slate-100 border-slate-600" : "bg-white"
          }`}
          data-testid="summary-pending"
        >
          <p className="font-display font-extrabold text-2xl tabular-nums text-slate-700">
            {summary.pending ?? 0}
          </p>
          <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mt-0.5">Pending</p>
          <p className="text-[10px] text-slate-400 mt-0.5">Awaiting attendance</p>
        </Card>

        {/* 6. Collect ₹X Card (Real-time Pending Present Session Charges) */}
        <Card
          onClick={() => setStatusFilter("pending_payment")}
          className={`p-3 text-center cursor-pointer transition-all hover:shadow-sm border-2 ${
            statusFilter === "pending_payment"
              ? "ring-2 ring-emerald-600 bg-emerald-100/70 border-emerald-600"
              : collectX > 0
              ? "bg-amber-50/70 border-amber-300"
              : "bg-emerald-50/50 border-emerald-200"
          }`}
          data-testid="summary-collect-x"
        >
          <p className={`font-display font-extrabold text-2xl tabular-nums ${collectX > 0 ? "text-amber-700" : "text-emerald-700"}`}>
            {inr(collectX)}
          </p>
          <p className="text-[10px] text-slate-700 font-bold uppercase tracking-wider mt-0.5">
            Collect {inr(collectX)}
          </p>
          <p className="text-[10px] text-slate-500 mt-0.5">
            {pendingCollectPatients.length} present awaiting payment
          </p>
        </Card>
      </div>

      {/* Search & Filter Toolbar */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            data-testid="clinic-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by patient name, phone or Patient ID..."
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-44">
            <SelectValue placeholder="Status Filter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="present">Present</SelectItem>
            <SelectItem value="absent">Absent</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
            <SelectItem value="rescheduled">Rescheduled</SelectItem>
          </SelectContent>
        </Select>
        <Select value={serviceFilter} onValueChange={setServiceFilter}>
          <SelectTrigger className="w-full sm:w-36">
            <SelectValue placeholder="Service" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Services</SelectItem>
            <SelectItem value="clinic">Clinic</SelectItem>
            <SelectItem value="home">Home Visit</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Attendance Cards List */}
      {filtered.length === 0 ? (
        <EmptyState
          icon={CalendarClock}
          title="No expected patients match your filter"
          subtitle="Expected patients are auto-generated from active treatment status, schedules, and next visit dates. Paused, completed, and rescheduled patients are excluded."
        />
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          {filtered.map((a) => {
            const isPending = a.status === "pending";
            const isPresent = a.status === "present";
            const isBusy = busy === a.id + "present";

            return (
              <Card
                key={a.id}
                data-testid={`attendance-card-${a.patient_code}`}
                className={`p-4 hover:shadow-md transition-all border-slate-200 flex flex-col justify-between relative overflow-hidden bg-white ${
                  isPresent ? "border-emerald-300 ring-1 ring-emerald-100" : ""
                }`}
              >
                {/* Status Indicator Bar */}
                <div
                  className={`absolute top-0 left-0 right-0 h-1.5 ${
                    isPresent
                      ? "bg-emerald-500"
                      : a.status === "absent"
                      ? "bg-red-500"
                      : a.status === "cancelled" || a.status === "rescheduled"
                      ? "bg-indigo-500"
                      : "bg-slate-300"
                  }`}
                />
                <div>
                  <div className="flex items-start gap-3">
                    <Avatar
                      className="h-10 w-10 cursor-pointer"
                      onClick={() => navigate(`/patients/${a.patient_id}`)}
                    >
                      <AvatarFallback className="bg-bk-lightGreen text-bk-green font-semibold text-sm">
                        {initials(a.patient_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p
                          className="font-semibold text-slate-800 truncate cursor-pointer hover:text-bk-blue"
                          onClick={() => navigate(`/patients/${a.patient_id}`)}
                        >
                          {a.patient_name}
                        </p>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <StatusBadge status={a.status} source={a.source} />
                          {isAdmin && !isPending && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openCorrectModal(a)}
                              className="h-6 px-1.5 text-xs text-slate-400 hover:text-bk-blue"
                              title="Correct Attendance (Admin Only)"
                            >
                              <Edit3 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 truncate">
                        {a.problem} · <span className="font-mono">{a.patient_code}</span>
                      </p>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap text-xs">
                        <StatusBadge status={a.treatment_status || "active"} />
                        <span className="text-slate-400 tabular-nums">Fee {inr(a.session_fee)}</span>
                        <AccountBalanceBadge balance={a.account_balance ?? a.advance_balance} />
                        {a.schedule && (
                          <Badge variant="outline" className="text-[10px] py-0 border-slate-200 text-slate-500 uppercase">
                            {a.schedule}
                          </Badge>
                        )}
                        {a.next_visit_date && (
                          <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200">
                            Next: {a.next_visit_date}
                          </Badge>
                        )}
                      </div>
                      {a.notes && (
                        <p className="text-xs text-slate-500 italic mt-1 bg-slate-50 px-2 py-0.5 rounded border border-slate-100">
                          Note: {a.notes}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Quick Attendance Action Buttons */}
                <div className="pt-3 border-t border-slate-100 mt-3 flex flex-wrap items-center gap-1.5">
                  {isPending ? (
                    <>
                      <Button
                        size="sm"
                        disabled={!!busy}
                        onClick={() => markPresent(a)}
                        data-testid={`mark-present-${a.patient_code}`}
                        className="flex-1 h-8 bg-bk-green hover:bg-emerald-700 text-white font-medium text-xs"
                      >
                        {isBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Check className="h-3.5 w-3.5 mr-1" />Present</>}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!!busy}
                        onClick={() => openAbsentModal(a)}
                        data-testid={`mark-absent-${a.patient_code}`}
                        className="flex-1 h-8 text-bk-danger border-red-200 hover:bg-red-50 text-xs"
                      >
                        <X className="h-3.5 w-3.5 mr-1" />Absent
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!!busy}
                        onClick={() => openCancelModal(a)}
                        data-testid={`mark-cancelled-${a.patient_code}`}
                        className="h-8 px-2 text-slate-600 hover:bg-slate-100"
                        title="Cancel Session"
                      >
                        <Ban className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!!busy}
                        onClick={() => openReschedule(a)}
                        className="h-8 px-2 text-indigo-600 hover:bg-indigo-50"
                        title="Reschedule Session"
                      >
                        <CalendarClock className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!!busy}
                        onClick={() => openComboModal(a)}
                        className="h-8 px-2 text-bk-blue hover:bg-blue-50"
                        title="Present + Complete Treatment"
                      >
                        <CheckCircle2 className="h-3.5 w-3.5" />
                      </Button>
                    </>
                  ) : (
                    <>
                      {isPresent && Number(a.account_balance ?? 0) < 0 && (
                        <Button
                          size="sm"
                          onClick={() => {
                            setSelectedForPayment({
                              id: a.patient_id,
                              name: a.patient_name,
                              patient_id: a.patient_code,
                              account_balance: a.account_balance,
                              session_fee: a.session_fee,
                            });
                            setDefaultPaymentAmount(Math.abs(Number(a.account_balance)));
                            setPaymentOpen(true);
                          }}
                          className="h-8 text-xs bg-rose-600 hover:bg-rose-700 text-white font-semibold gap-1 shadow-sm"
                        >
                          <Wallet className="h-3.5 w-3.5" /> Collect {inr(Math.abs(Number(a.account_balance)))}
                        </Button>
                      )}
                      {isPresent && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedForTreatment(a);
                            setTreatmentOpen(true);
                          }}
                          className="h-8 text-xs text-bk-blue border-blue-200 hover:bg-blue-50 gap-1"
                        >
                          <Stethoscope className="h-3.5 w-3.5" /> Treatment
                        </Button>
                      )}
                      {isPresent && a.treatment_status !== "completed" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openComboModal(a)}
                          className="h-8 text-xs text-bk-green border-emerald-200 hover:bg-emerald-50 gap-1"
                        >
                          <CheckCircle2 className="h-3.5 w-3.5" /> Complete
                        </Button>
                      )}
                    </>
                  )}

                  {/* Communication shortcuts */}
                  <div className="flex items-center gap-1 ml-auto">
                    <a href={telLink(a.phone)}>
                      <Button size="sm" variant="ghost" className="h-8 px-2 text-bk-blue hover:bg-blue-50">
                        <Phone className="h-3.5 w-3.5" />
                      </Button>
                    </a>
                    <a href={waLink(a.whatsapp || a.phone)} target="_blank" rel="noreferrer">
                      <Button size="sm" variant="ghost" className="h-8 px-2 text-bk-green hover:bg-emerald-50">
                        <MessageCircle className="h-3.5 w-3.5" />
                      </Button>
                    </a>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* 1. Treatment Record Dialog */}
      <TreatmentRecordDialog
        open={treatmentOpen}
        onOpenChange={setTreatmentOpen}
        attendance={selectedForTreatment}
        onSaved={load}
      />

      {/* 2. Absent Modal */}
      <Dialog open={absentOpen} onOpenChange={setAbsentOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700 font-display">
              <X className="h-5 w-5 text-amber-600" /> Mark Patient Absent
            </DialogTitle>
          </DialogHeader>
          {selectedForAbsent && (
            <div className="space-y-3 py-2">
              <p className="text-sm font-medium text-slate-700">
                Patient: <span className="font-bold text-slate-900">{selectedForAbsent.patient_name}</span> ({selectedForAbsent.patient_code})
              </p>
              <div className="space-y-1.5">
                <Label>Reason for Absence <span className="text-red-500">*</span></Label>
                <Select value={absentReason} onValueChange={setAbsentReason}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ABSENT_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Additional Notes (Optional)</Label>
                <Input
                  placeholder="e.g. Next call tomorrow morning..."
                  value={absentNote}
                  onChange={(e) => setAbsentNote(e.target.value)}
                />
              </div>
              <p className="text-xs text-slate-500 bg-amber-50 p-2.5 rounded border border-amber-100">
                An absence follow-up will be recorded for tracking. No fees or advance balance are deducted.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAbsentOpen(false)}>Cancel</Button>
            <Button
              onClick={handleConfirmAbsent}
              disabled={markingAbsent}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {markingAbsent ? "Marking..." : "Confirm Absent"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 3. Cancelled Modal */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-700 font-display">
              <Ban className="h-5 w-5 text-slate-500" /> Cancel Today's Session
            </DialogTitle>
          </DialogHeader>
          {selectedForCancel && (
            <div className="space-y-3 py-2">
              <p className="text-sm font-medium text-slate-700">
                Patient: <span className="font-bold text-slate-900">{selectedForCancel.patient_name}</span> ({selectedForCancel.patient_code})
              </p>
              <div className="space-y-1.5">
                <Label>Cancellation Reason <span className="text-red-500">*</span></Label>
                <Select value={cancelReason} onValueChange={setCancelReason}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CANCEL_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Notes (Optional)</Label>
                <Input
                  placeholder="e.g. Clinic holiday / rain..."
                  value={cancelNote}
                  onChange={(e) => setCancelNote(e.target.value)}
                />
              </div>
              <p className="text-xs text-slate-500 bg-slate-50 p-2.5 rounded border border-slate-200">
                Cancelled sessions do not generate absent follow-ups and do not deduct fees.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)}>Cancel</Button>
            <Button
              onClick={handleConfirmCancel}
              disabled={markingCancel}
              className="bg-slate-700 hover:bg-slate-800 text-white"
            >
              {markingCancel ? "Cancelling..." : "Confirm Cancel"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 4. Reschedule Modal */}
      <Dialog open={rescheduleOpen} onOpenChange={setRescheduleOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-indigo-700">
              <CalendarClock className="h-5 w-5" /> Reschedule Session
            </DialogTitle>
          </DialogHeader>
          {selectedAtt && (
            <div className="space-y-3 py-2">
              <p className="text-sm font-medium text-slate-700">
                Rescheduling for: <span className="text-bk-blue font-bold">{selectedAtt.patient_name}</span> ({selectedAtt.patient_code})
              </p>
              <div className="space-y-1.5">
                <Label>New Visit Date <span className="text-red-500">*</span></Label>
                <Input
                  type="date"
                  value={newVisitDate}
                  min={today}
                  onChange={(e) => setNewVisitDate(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>New Visit Time (Optional)</Label>
                <Input
                  type="time"
                  value={newVisitTime}
                  onChange={(e) => setNewVisitTime(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Reason / Note</Label>
                <Input
                  placeholder="e.g. Patient traveling, requested evening slot..."
                  value={rescheduleReason}
                  onChange={(e) => setRescheduleReason(e.target.value)}
                />
              </div>
              <div className="flex items-start space-x-2 pt-2 bg-indigo-50/60 p-3 rounded-lg border border-indigo-100">
                <Checkbox
                  id="exclude_until_modal"
                  checked={excludeUntilDate}
                  onCheckedChange={setExcludeUntilDate}
                />
                <div className="space-y-1 leading-none">
                  <label htmlFor="exclude_until_modal" className="text-xs font-semibold text-indigo-900 cursor-pointer">
                    Exclude patient from expected attendance until the new visit date
                  </label>
                  <p className="text-[11px] text-indigo-600">
                    Prevents patient from falsely appearing as Pending or Auto-Absent on intermediate days.
                  </p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRescheduleOpen(false)}>Cancel</Button>
            <Button onClick={handleReschedule} disabled={rescheduling} className="bg-indigo-600 hover:bg-indigo-700 text-white">
              {rescheduling ? "Rescheduling..." : "Confirm Reschedule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 5. Present + Complete Treatment Combo Modal */}
      <Dialog open={comboOpen} onOpenChange={setComboOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-bk-green">
              <CheckCircle2 className="h-5 w-5" /> Present + Complete Treatment
            </DialogTitle>
          </DialogHeader>
          {selectedForCombo && (
            <div className="space-y-3 py-2">
              <p className="text-sm font-medium text-slate-700">
                Completing for: <span className="font-bold text-bk-blue">{selectedForCombo.patient_name}</span> ({selectedForCombo.patient_code})
              </p>
              <div className="space-y-1.5">
                <Label>Completion Summary / Outcome</Label>
                <Input
                  placeholder="e.g. Pain relieved, full range of motion restored..."
                  value={comboSummary}
                  onChange={(e) => setComboSummary(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Final Pain Score (0–10)</Label>
                <Input
                  type="number"
                  min="0"
                  max="10"
                  placeholder="0"
                  value={comboPainScore}
                  onChange={(e) => setComboPainScore(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Home Exercise Advice / Clinical Advice</Label>
                <Textarea
                  placeholder="e.g. Continue lumbar extension exercises daily..."
                  rows={2}
                  value={comboAdvice}
                  onChange={(e) => setComboAdvice(e.target.value)}
                />
              </div>
              <div className="flex items-center space-x-2 pt-1">
                <Checkbox
                  id="combo_wa"
                  checked={comboSendWa}
                  onCheckedChange={setComboSendWa}
                />
                <label htmlFor="combo_wa" className="text-xs font-medium text-slate-700 cursor-pointer">
                  Send treatment completion WhatsApp message to patient
                </label>
              </div>
              <p className="text-[11px] text-slate-500 bg-emerald-50 p-2.5 rounded border border-emerald-200">
                This marks today's attendance Present, sets treatment status to Completed, and excludes patient from future expected attendance.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setComboOpen(false)}>Cancel</Button>
            <Button
              onClick={handleConfirmCombo}
              disabled={savingCombo}
              className="bg-bk-green hover:bg-emerald-700 text-white"
            >
              {savingCombo ? "Completing..." : "Confirm & Complete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 6. Admin Attendance Correction Modal */}
      <Dialog open={correctOpen} onOpenChange={setCorrectOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-bk-blue">
              <ShieldAlert className="h-5 w-5 text-amber-600" /> Correct Attendance Record
            </DialogTitle>
          </DialogHeader>
          {selectedForCorrect && (
            <div className="space-y-3 py-2">
              <p className="text-sm font-medium text-slate-700">
                Patient: <span className="font-bold text-slate-900">{selectedForCorrect.patient_name}</span> ({selectedForCorrect.patient_code})
              </p>
              <div className="bg-slate-50 p-2.5 rounded border text-xs text-slate-600 flex justify-between">
                <span>Current Status: <strong className="uppercase text-slate-800">{selectedForCorrect.status}</strong></span>
                <span>Date: <strong>{selectedForCorrect.date}</strong></span>
              </div>
              <div className="space-y-1.5">
                <Label>New Corrected Status <span className="text-red-500">*</span></Label>
                <Select value={correctStatus} onValueChange={setCorrectStatus}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="present">Present</SelectItem>
                    <SelectItem value="absent">Absent</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="rescheduled">Rescheduled</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Mandatory Correction Reason <span className="text-red-500">*</span></Label>
                <Input
                  placeholder="e.g. Marked absent by mistake, attended 4 PM slot..."
                  value={correctReason}
                  onChange={(e) => setCorrectReason(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Additional Note</Label>
                <Input
                  placeholder="Optional details..."
                  value={correctNote}
                  onChange={(e) => setCorrectNote(e.target.value)}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCorrectOpen(false)}>Cancel</Button>
            <Button
              onClick={handleConfirmCorrect}
              disabled={savingCorrect || !correctReason.trim()}
              className="bg-bk-blue hover:bg-blue-700 text-white"
            >
              {savingCorrect ? "Saving..." : "Save Correction"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 7. Auto-Absent Cutoff Confirmation with Exact Count */}
      <Dialog open={cutoffOpen} onOpenChange={setCutoffOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-bk-danger">
              <Zap className="h-5 w-5 text-bk-danger" /> Run Auto-Absent Cutoff
            </DialogTitle>
            <DialogDescription>
              Mark all unmarked pending patients as Automatic Absent for {fmtDate(today)}.
            </DialogDescription>
          </DialogHeader>
          <div className="py-3">
            {loadingCutoffPreview ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-bk-danger" />
              </div>
            ) : cutoffPreview ? (
              <div className="space-y-2">
                <div className="bg-red-50 border border-red-200 p-3.5 rounded-lg">
                  <p className="text-sm font-semibold text-red-900">
                    {cutoffPreview.pending_count} pending patient{cutoffPreview.pending_count !== 1 ? "s" : ""} will be marked Automatic Absent.
                  </p>
                  <p className="text-xs text-red-700 mt-1">
                    This action will create follow-ups for active patients who did not attend today. Paused and completed patients are protected.
                  </p>
                </div>
                {cutoffPreview.pending_patients?.length > 0 && (
                  <div className="max-h-36 overflow-y-auto divide-y text-xs text-slate-700 bg-slate-50 rounded border p-2">
                    {cutoffPreview.pending_patients.map((p) => (
                      <div key={p.id} className="py-1 flex justify-between">
                        <span className="font-medium">{p.patient_name}</span>
                        <span className="text-slate-400">{p.patient_code}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCutoffOpen(false)}>Cancel</Button>
            <Button
              onClick={handleExecuteCutoff}
              disabled={runningCutoff || loadingCutoffPreview || cutoffPreview?.pending_count === 0}
              className="bg-bk-danger hover:bg-red-700 text-white"
            >
              {runningCutoff ? "Running..." : "Confirm Auto-Absent"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 7. Add Money / Collect Payment Dialog */}
      <PaymentDialog
        open={paymentOpen}
        onOpenChange={setPaymentOpen}
        patient={selectedForPayment}
        defaultAmount={defaultPaymentAmount}
        onSaved={load}
      />
    </div>
  );
}


